export type MaturityLevel = {
  level: number;
  name: string;
  documentation_expectations: string;
  implementation_expectations: string;
};

export type CyFunActivity = {
  code: string;
  name: string;
};

export type CyFunCategory = {
  code: string;
  id: string;
  activity_code: string;
  name: string;
};

export type CyFunSubcategory = {
  code: string;
  id: string;
  category_code: string;
  name: string;
};

export type CyFunRequirement = {
  code: string;
  id: string;
  subcategory_code: string;
  assurance_level: string;
  name: string;
  is_governance_measure: string;
  is_key_measure: string;
};

export type QuestionnaireItem = {
  item_index: number;
  assurance_level: string;
  element_type: ElementType;
  element_name: string;
  element_description: string;
  linked_activity_codes: string[];
  linked_category_codes: string[];
  linked_subcategory_codes: string[];
  linked_requirement_codes: string[];
};

export type ElementType =
  | "Politici"
  | "Proceduri"
  | "Procese"
  | "Inregistrari"
  | "Utilitare";

export type MaturityEvaluationRubric = {
  requirement_code: string;
  maturity_level: number;
  documentation: RubricDetail;
  implementation: RubricDetail;
};

export type RubricDetail = {
  description: string;
  policies: string[];
  procedures: string[];
  processes: string[];
  records: string[];
  tools: string[];
};

export type EvalState = "NOT_STARTED" | "PRELIM_COMPLETE" | "FULL_COMPLETE";

export type PageView = "summary" | "preliminary" | "full-eval" | "assisted";

export type ScoringAlgorithm = {
  thresholds: {
    category_minimum: number;
    total_maturity_minimum: number;
  };
  assurance_levels: AssuranceLevelConfig[];
};

export type AssuranceLevelConfig = {
  code: string;
  name: string;
  display_name: string;
  requirement_count: number;
  requirement_filter: {
    field: string;
    include_values: string[];
  };
  key_measures: string[];
  key_measure_count: number;
  key_measure_minimum: number;
};

export type ValidationResult = {
  key_measures_passed: boolean;
  categories_passed: boolean;
  total_passed: boolean;
  level_achieved: boolean;
  failed_key_measures: string[];
  failed_categories: string[];
};

export type MaturityEvaluation = {
  id: string;
  form_session_id: string;
  eval_state: EvalState;
  assurance_level: string;
  target_score: number;
  preliminary_responses: Record<string, boolean>;
  preliminary_scores: Record<string, number> | null;
  full_responses: Record<string, number>;
  full_notes: Record<string, string>;
  full_scores: Record<string, number> | null;
  overall_score: number | null;
  key_measures_passed: boolean | null;
  categories_passed: boolean | null;
  level_achieved: boolean | null;
  preliminary_finalized_at: string | null;
  full_finalized_at: string | null;
  created_at: string;
  updated_at: string;
};

export type MaturityFrameworkResponse = {
  maturity_levels: MaturityLevel[];
  activities: CyFunActivity[];
  categories: CyFunCategory[];
  subcategories: CyFunSubcategory[];
  requirements: CyFunRequirement[];
};

export type QuestionnaireResponse = {
  items: QuestionnaireItem[];
  total_count: number;
  assurance_level: string;
};

export type EvaluationRubricsResponse = {
  category_code: string;
  requirements: Array<{
    code: string;
    name: string;
    subcategory_code: string;
    assurance_level: string;
  }>;
  maturity_evaluations: MaturityEvaluationRubric[];
};

export type DomainMeta = {
  id: string;
  name: string;
  color: string;
  pill: string;
  bg: string;
  border: string;
};

export type SaveStatus = "idle" | "saving" | "saved" | "error";

export const SECTION_ORDER: ElementType[] = [
  "Politici",
  "Proceduri",
  "Procese",
  "Inregistrari",
  "Utilitare",
];

export const SECTION_ICONS: Record<ElementType, string> = {
  Politici: "📋",
  Proceduri: "📝",
  Procese: "⚙️",
  Inregistrari: "🗂️",
  Utilitare: "🛡️",
};

export const SECTION_QUESTIONS: Record<ElementType, string> = {
  Politici:
    "Organizația dumneavoastră are definite politici de securitate cibernetică?",
  Proceduri:
    "Organizația dumneavoastră are proceduri documentate de securitate?",
  Procese: "Organizația dumneavoastră are procese formalizate de securitate?",
  Inregistrari:
    "Organizația dumneavoastră menține înregistrări și evidențe de securitate?",
  Utilitare:
    "Organizația dumneavoastră utilizează soluții/utilitare de securitate?",
};
